interface ConflictInfo {
  customerName: string
  service: string
  time: string
  duration: number
}

interface AlternativeSlot {
  date: string
  time: string
  dayName: string
  dateDisplay: string
  timeDisplay: string
  stylistName?: string
}

interface ConflictAlertModalProps {
  conflict: ConflictInfo
  suggestions: AlternativeSlot[]
  onSelectSlot: (slot: AlternativeSlot) => void
  onClose: () => void
}

export function ConflictAlertModal({
  conflict,
  suggestions,
  onSelectSlot,
  onClose
}: ConflictAlertModalProps) {
  console.log('[ConflictAlertModal] Rendering with:', {
    conflict,
    suggestions,
    suggestionsLength: suggestions?.length,
    suggestionsType: typeof suggestions,
    suggestionsIsArray: Array.isArray(suggestions)
  })
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 p-4 rounded-t-lg">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900">
                Time Slot Unavailable
              </h3>
              <p className="text-sm text-red-700 mt-1">
                This time slot is already booked
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conflict details */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="text-sm">
            <span className="font-medium text-gray-700">Current booking:</span>
            <div className="mt-2 bg-white rounded-lg p-3 border border-gray-200">
              <div className="font-semibold text-gray-900">{conflict.customerName}</div>
              <div className="text-gray-600 mt-1">{conflict.service}</div>
              <div className="text-gray-500 text-xs mt-1">
                {conflict.time} ({conflict.duration} minutes)
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions */}
        <div className="p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Next Available Slots:
          </h4>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {suggestions.map((slot, index) => (
              <button
                key={slot.date + slot.time}
                onClick={() => onSelectSlot(slot)}
                className="w-full p-3 border-2 border-gray-200 rounded-lg 
                           hover:border-blue-500 hover:bg-blue-50 
                           transition-all duration-150 text-left group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900 group-hover:text-blue-700">
                      {slot.dayName}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {slot.dateDisplay} at {slot.timeDisplay}
                    </div>
                    {slot.stylistName && (
                      <div className="text-xs text-blue-600 mt-1 font-medium">
                        with {slot.stylistName}
                      </div>
                    )}
                  </div>

                  {index === 0 && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      Soonest
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {suggestions.length === 0 && (
            <div className="text-center py-6 text-gray-500">
              No available slots found in the next 2 weeks
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg 
                       hover:bg-gray-100 text-gray-700 font-medium transition-colors"
          >
            Customer Will Call Back
          </button>
        </div>
      </div>
    </div>
  )
}

export type { ConflictInfo, AlternativeSlot }
